import { Drawer } from '@/components/common';
import { RadioPaymentSelector } from '@/components/subflow-v3/components/RadioPaymentSelector';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import { useStepTracking } from '@/hooks/subFlowAnalytics';
import { PaymentOption } from '@/hooks/usePaymentOptions';
import { useStartCheckout } from '@/hooks/useStartCheckout';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';
import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';
import { FC, useCallback, useState } from 'react';

export interface PlayKitPaymentOptionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  t: TypeSubFlowV3Translations;
}

export const PlayKitPaymentOptionDrawer: FC<
  PlayKitPaymentOptionDrawerProps
> = ({ isOpen, onClose, t }) => {
  const [isLoading, setIsLoading] = useState(false);

  const { data, api } = useSubFlowV3Data();

  const { continueToCheckout, isCheckoutLoading } = useStartCheckout(t, 'v3');

  const { onStepComplete } = useStepTracking('v3');

  const finishStep = useCallback(async () => {
    setIsLoading(true);

    trackItemClickedEvent({
      url: window.location.href,
      type: CLICKED_ITEM_TYPES.BUTTON,
      pageLocation: PAGE_LOCATIONS.SUBSCRIPTION_FLOW.ORDER_REVIEW,
      cta: CTA_NAMES.CONTINUE_TO_CHECKOUT,
    });

    onStepComplete(data);

    await continueToCheckout(
      {
        callback: () => {
          setIsLoading(false);
        },
      },
      false,
      true,
    );
  }, [onStepComplete, data, continueToCheckout]);

  const handleSubmit = useCallback(
    (paymentOption?: PaymentOption | null) => {
      if (!paymentOption) return;

      api.selectPaymentOption(paymentOption);

      finishStep();
    },

    [api, finishStep],
  );

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={t.paymentDrawerTitle}
      customClassNames="mb-24"
    >
      <RadioPaymentSelector
        data={data}
        api={api}
        t={t}
        handleSubmit={handleSubmit}
        loadingCheckout={isCheckoutLoading || isLoading}
        drawerStyles
      />
    </Drawer>
  );
};
